/**
 * PermisosResolver
 *
 * Motor de resolución de permisos de embarcaciones para apoderados.
 *
 * Tras la normalización (2026-06-26) permiso_control controla ÚNICAMENTE
 * embarcaciones y la columna `modulo` guarda directamente el id de la
 * embarcación, sin el prefijo de módulo `2;`. Ya no hay dimensión de menús
 * ni jerarquía de módulos (se eliminaron buildModuleChain y resolveAccess).
 *
 * Convenio de entradas IPermisoControl:
 *   modulo  = "{id}"          → id de la embarcación
 *   permiso = FULL (0x1111)   → acceso concedido (vigente)
 *   permiso = 0 (NONE)        → denegación; se conserva como historial de revocación
 *   prioridad                 → ya no influye en la resolución (legado)
 *
 * Regla única: un apoderado accede a la embarcación {id} sii existe una fila
 * modulo="{id}" con permiso>0. La ausencia y los deny equivalen a sin acceso
 * (deny por defecto implícito).
 *
 * Compatibilidad de lectura: mientras la migración de datos no se haya corrido,
 * pueden coexistir filas en el formato viejo "2;{id}". Las funciones de lectura
 * normalizan ambos formatos con `embarcacionIdDeModulo`; las de escritura
 * generan siempre el formato nuevo "{id}".
 *
 * El Representante Legal NO pasa por aquí: su acceso es espejado al armador
 * (capacidades plenas), resuelto antes (RoleCapacitiesManager / APPROVED_LEGAL).
 */

import { IPermisoControl, ActionsPermissions } from '../api-services/RepresentantesServices'

/**
 * Configuración de permisos pre-asignados al crear un nuevo representante.
 * Se almacena en DB ligada a la solicitud (PENDING); toma efecto cuando EPV aprueba.
 */
export interface PermisosAddConfig {
  /** IDs de embarcaciones asignadas explícitamente. Para un apoderado de armador debe haber al
   *  menos una; vacío equivale a apoderado Inactivo (sin acceso a ninguna). */
  embarcaciones: number[]
  /** true cuando el selector de embarcaciones fue mostrado (armador tiene embarcaciones asignadas) */
  armadorTieneSelector: boolean
}

/**
 * Normaliza el `modulo` de una fila a su id de embarcación numérico.
 * Acepta el formato nuevo "{id}" y el viejo "2;{id}" (compatibilidad durante la
 * migración). Devuelve null si no representa una embarcación (p.ej. el ancla "2"
 * vieja o una fila de menú "0"/"7").
 */
export function embarcacionIdDeModulo(modulo: string | number): number | null {
  const s = String(modulo)
  // Formato viejo "2;{id}": tomar el id tras el ';'. Formato nuevo "{id}": tal cual.
  // El ancla vieja "2" (sin ';') no es una embarcación: se descarta abajo.
  if (s.includes(';')) {
    const parts = s.split(';')
    if (parts[0] !== '2' || parts.length !== 2) return null
    const id = parseInt(parts[1], 10)
    return isNaN(id) ? null : id
  }
  // Sin ';': en el formato nuevo el modulo ES el id. El ancla vieja "2" y las
  // filas de menú ("0","7") nunca traen permiso>0, así que los lectores que
  // filtran por permiso>0 ya las excluyen; aun así, no se pueden distinguir de
  // una embarcación con ese id por el string solo. Por eso quienes necesiten
  // excluir el ancla deben filtrar por permiso>0 (allow) antes de llamar aquí.
  const id = parseInt(s, 10)
  return isNaN(id) ? null : id
}

/**
 * Encuentra la fila de permiso de una embarcación, tolerando el formato nuevo "{id}"
 * y el viejo "2;{id}" (compatibilidad durante la migración). Da prioridad al específico
 * viejo "2;{id}" para no confundir el ancla vieja "2" con la embarcación de id 2.
 *
 * Usar en los escritores (toggle de asignación) para localizar la fila a actualizar; si
 * devuelve undefined hay que insertar una nueva en formato "{id}".
 */
export function findEntradaEmbarcacion<T extends { modulo: string | number }>(
  entries: T[],
  embarcacionId: number,
): T | undefined {
  return (
    entries.find((e) => String(e.modulo) === `2;${embarcacionId}`) ??
    entries.find((e) => String(e.modulo) === String(embarcacionId))
  )
}

/**
 * Resuelve si un APODERADO tiene acceso a una embarcación concreta.
 * Regla: existe fila para esa embarcación con permiso>0.
 *
 * @param entries            Permisos del representante para su empresa
 * @param embarcacionId      ID de la embarcación a evaluar
 * @param _otrosRepsActivos  Legado: ya no influye en la resolución. Se conserva en la
 *                           firma para no tocar todos los call-sites; ignorado.
 */
export function resolveEmbarcacionAccess(
  entries: IPermisoControl[],
  embarcacionId: number,
  _otrosRepsActivos = 0,
): boolean {
  return entries.some((e) => e.permiso > 0 && embarcacionIdDeModulo(e.modulo) === embarcacionId)
}

/** Modo informativo para mostrar en UI. No afecta la lógica de resolución. */
export type PermisosMode = 'OPEN' | 'BLACKLIST' | 'WHITELIST'

export function detectMode(entries: IPermisoControl[]): PermisosMode {
  if (entries.length === 0) return 'OPEN'
  const hasApprovals = entries.some((e) => e.permiso > 0)
  const hasDenials = entries.some((e) => e.permiso === ActionsPermissions.NONE)
  if (hasApprovals) return 'WHITELIST'
  if (hasDenials) return 'BLACKLIST'
  return 'OPEN'
}

/** IDs de embarcaciones explícitamente asignadas (con permiso>0) en los permisos. */
export function getEmbarcacionesAsignadas(entries: IPermisoControl[]): number[] {
  return entries
    .filter((e) => e.permiso > 0)
    .map((e) => embarcacionIdDeModulo(e.modulo))
    .filter((id): id is number => id !== null)
}

/**
 * RP-25 — Un APODERADO aprobado carece de embarcaciones efectivas (→ se trata como Inactivo)
 * cuando no tiene ninguna embarcación asignada positivamente. Sin "modo implícito", la regla es
 * directa: 0 embarcaciones asignadas = Inactivo.
 *
 * El Representante Legal NO se evalúa con esta función: su estado Activo deriva de APPROVED_LEGAL,
 * no de la matriz de embarcaciones (ver getRepresentanteEstado).
 *
 * @param permisosRep         Permisos (IPermisoControl) del representante.
 * @param _repsActivosArmador Legado: ya no influye. Ignorado; se conserva por compatibilidad de firma.
 */
export function repAprobadoSinEmbarcaciones(permisosRep: IPermisoControl[], _repsActivosArmador = 0): boolean {
  return getEmbarcacionesAsignadas(permisosRep).length === 0
}

/**
 * Devuelve los IDs de embarcaciones a las que el representante REALMENTE tiene acceso.
 * Usar para inicializar el selector de embarcaciones en la UI.
 *
 * @param entries           Permisos del representante
 * @param todasLasIds       IDs de todas las embarcaciones del armador
 * @param _otrosRepsActivos Legado: ya no influye. Ignorado.
 */
export function getEmbarcacionesAccesiblesResueltas(
  entries: IPermisoControl[],
  todasLasIds: number[],
  _otrosRepsActivos = 0,
): number[] {
  return todasLasIds.filter((id) => resolveEmbarcacionAccess(entries, id))
}

/** Construye el conjunto de IPermisoControl a insertar para una nueva designación.
 *
 * Genera una fila allow (FULL) por embarcación asignada, en formato "{id}". No
 * crea ancla ni entradas de módulo (el deny por defecto es implícito).
 *
 * @param solicitudId   ID de la solicitud RepresentanteArmador recién creada
 * @param embarcaciones IDs de embarcaciones asignadas. Vacío = apoderado sin naves (Inactivo).
 *                      null se usa para reps sin matriz de embarcaciones (p.ej. proveedor): no genera entradas.
 */
export function buildPermisosParaDesignacion(
  solicitudId: number,
  embarcaciones: number[] | null,
): Omit<IPermisoControl, 'id'>[] {
  if (embarcaciones === null) return []
  const ahora = Date.now()
  return embarcaciones.map((embId) => ({
    representante: solicitudId,
    prioridad: ahora,
    modulo: String(embId),
    permiso: ActionsPermissions.FULL,
  }))
}
